import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Col, Form, Row, Modal } from "react-bootstrap";
import { EventService, TicketService } from "../../../services";
import { toast } from "react-toastify";
import { injectModels } from "../../../redux/injectModels";
import { useParams } from "react-router-dom";
import { handleConsoleError } from "../../../utils/common-methods";

const CreateTicketRequest = {
  id: null,
  eventId: null,
  name: "",
  price: "",
  maxQuantity: "",
  allowPerUser: "",
  startTime: "",
  endTime: "",
  isPublished: false,
  ticketPic: null,
};

const CreateTicket = (props) => {
  //const selectedEvent = EventService.getSelectedEvent();
  const { eventId } = useParams();
  const [ticketData, setTicketData] = useState({ ...CreateTicketRequest });
  const [eventData, setEventData] = useState(null);
  const [imageError, setImageError] = useState("");
  const [file, setFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isImageValid, setIsImageValid] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const today = new Date();
  const endDate = new Date(today.setHours(0, 0, 0, 0));
  endDate.setDate(endDate.getDate() + 1);
  const maxSize = 4 * 1024 * 1024;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    watch,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm();

  // ***** TICKETS DATA **************
  const getTickets = async () => {
    try {
      const res =
        (eventId && (await TicketService.getTicketById(eventId))) || {};
      const ticketMappedData = { ...CreateTicketRequest, ...res?.data };
      ticketMappedData.startTime = ticketMappedData?.startTime?.split("T")[0];
      ticketMappedData.endTime = ticketMappedData?.endTime?.split("T")[0];
      reset(ticketMappedData);
      setTicketData(ticketMappedData);
      if (ticketMappedData?.imageUrl) {
        setImagePreview(ticketMappedData?.imageUrl);
        setIsImageValid(true);
      }
    } catch (error) {
      const message = handleConsoleError(error);
      toast.error(error?.message || message);
    }
  };

  // *******GET BY EVENT **********
  const getEventByID = async () => {
    try {
      const response = await EventService.getEventById(eventId);
      if (response?.isSuccess) {
        setEventData(response?.data);
      }
    } catch (error) {
      const message = handleConsoleError(error);
      toast.error(error?.message || message);
    }
  };

  useEffect(() => {
    getTickets();
    getEventByID();
  }, []);

  useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (name === "startTime" || name === "endTime") {
        validateTimeRange(); // Re-run time range validation when dates change
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  const validateTimeRange = () => {
    const startDate = new Date(getValues("startTime")).setHours(0, 0, 0, 0);
    const endDate = new Date(getValues("endTime")).setHours(0, 0, 0, 0);

    if (endDate < startDate) {
      setError("endTime", {
        message: "End Time must be greater than or equals to Start Date",
      });
    } else {
      clearErrors("endTime");
    }
  };

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();
      data.eventId = eventId;
      data.isPublished = ticketData?.isPublished;
      data.id = ticketData?.id || null; // Use the existing ID or set it to null if not present

      formData.append("eventId", eventId);
      formData.append("isPublished", ticketData?.isPublished);
      if (data.id && data.id.length > 2) {
        formData.append("id", data?.id);
      }
      formData.append("name", data?.name);
      formData.append("price", data?.price);
      formData.append("maxQuantity", data?.maxQuantity);
      formData.append("allowPerUser", data?.allowPerUser);
      formData.append("startTime", data?.startTime);
      formData.append("endTime", data?.endTime);

      if (!imagePreview && (!data?.Image || !data?.Image[0])) {
        setImageError("Ticket image is required.");
        return;
      } else {
        setImageError(""); // Clear error if image exists
      }

      if (data?.Image && data?.Image[0]) {
        const file = data?.Image[0];
        const validTypes = ["image/jpeg", "image/png"];
        if (!validTypes.includes(file.type)) {
          setImageError("Invalid image format. Only JPG and PNG are allowed.");
          return;
        }

        formData.append("Image", file);
        // setValue("ticketPic", file); // Set the file in the form data
      }
      if (file) {
        formData.append("Image", file);
        //setValue("ticketPic", file);
      }
       // TODO Duplicate ticket creation issue.
      //props.application.setLoading(true);

      const response = await TicketService.createTicket(formData);
      if (response?.isSuccess) {
        await getTickets();
        props.application.setLoading(false);
        toast.success(response?.message);
      } else {
        props.application.setLoading(false);

        toast.error(response?.message);
      }
    } catch (error) {
      props.application.setLoading(false);
      const message = handleConsoleError(error);
      toast.error(error?.message || message);
    }
  };

  const handleChange = (e, cntName) => {
    let inputValue = e.target.value;
    // Remove all non-numeric characters except the decimal point
    inputValue = inputValue.replace(/[^0-9.]/g, "");

    // Ensure that only one decimal point is allowed
    inputValue = inputValue.replace(/(\..*?)\..*/g, "$1");

    // Update the state with the sanitized value
    setTicketData({ ...ticketData, cntName: inputValue });

    const numericPattern = /^[+]?[0-9]*\.?[0-9]+$/; // Allows positive numbers, integers, and decimals

    // Validate if the value matches the numeric pattern and is greater than 0
    if (
      inputValue === "" ||
      (numericPattern.test(inputValue) && parseFloat(inputValue) > 0)
    ) {
      clearErrors(cntName); // Clear error message if input is valid
    } else {
      setError(cntName, {
        message: "Please enter a valid numeric value greater than zero.",
      });
    }
  };

  const handleIsStatus = (event) => {
    setTicketData({
      ...ticketData,
      isPublished: event.target.checked,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    // formData.append("Image", file.get("image")); // Field name must match the property in C# model
    const validTypes = ["image/jpeg", "image/png"];

    if (file && validTypes.includes(file.type)) {
      if (file.size > maxSize) {
        setImageError(
          `File "${file.name}" exceeds the maximum size of ${maxSize /
            (1024 * 1024)}MB.`,
        );
        setIsImageValid(false);
        setFile(null);
        e.target.value = null;
      } else {
        setFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
          setIsImageValid(true);
          setImageError("");
        };
        reader.readAsDataURL(file);
      }
    } else {
      setIsImageValid(false);
      setImageError("Invalid image format. Only JPG and PNG are allowed.");
      setFile(null);
      e.target.value = null;
    }
  };

  return (
    <React.Fragment>
      <section className="subscription-banner p-0">
        <div className="container">
          <h3>Create Event Ticket</h3>
        </div>
      </section>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="name" className="mb-3">
            <Form.Label>
              Title<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              maxLength={100}
              // onChange={(event) => handleChange(event, "name")}

              type="text"
              {...register("name", {
                required: "Please enter ticket title",
              })}
              onChange={(e) => {
                const value = e.target.value;
                setValue(
                  "name",
                  value?.charAt(0)?.toUpperCase() + value?.slice(1),
                );
              }}
              defaultValue={ticketData?.name}
            />
            {errors.name && (
              <span className="text-danger">{errors.name?.message}</span>
            )}
          </Form.Group>

          <Form.Group as={Col} controlId="price" className="mb-3">
            <Form.Label>
              Price (£)<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              //

              {...register("price", {
                required: "Please enter ticket price",
                pattern: {
                  value: /^[+]?[0-9]*\.?[0-9]+$/,
                  message: "Please enter a valid number greater than zero.",
                },
              })}
              onChange={(event) => handleChange(event, "price")}
              defaultValue={ticketData?.price}
            />
            {errors.price && (
              <span className="text-danger">{errors.price?.message}</span>
            )}
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="maxQuantity" className="mb-3">
            <Form.Label>
              Total Available Tickets <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              maxLength="10"
              type="text"
              {...register("maxQuantity", {
                required: "Please enter available ticket count",
                pattern: {
                  value: /^[+]?[0-9]*\.?[0-9]+$/,
                  message: "Please enter a valid number greater than zero.",
                },
              })}
              onChange={(event) => handleChange(event, "maxQuantity")}
              defaultValue={ticketData.maxQuantity}
            />
            {errors.maxQuantity && (
              <span className="text-danger">{errors.maxQuantity?.message}</span>
            )}
          </Form.Group>
          <Form.Group as={Col} controlId="allowPerUser" className="mb-3">
            <Form.Label>
              Maximum Allowed Per Person <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              maxLength="10"
              type="text"
              {...register("allowPerUser", {
                required: "Please enter max allowed ticket per user",
                pattern: {
                  value: /^[+]?[0-9]*\.?[0-9]+$/,
                  message: "Please enter a valid number greater than zero.",
                },
                validate: (value) => {
                  const totalTickets = parseFloat(watch("maxQuantity"));
                  const maxAllowed = parseFloat(value);

                  if (maxAllowed > totalTickets) {
                    return " Maximum Allowed cannot be greater than Total tickets";
                  }
                  return true; // Validation passes
                },
              })}
              onChange={(event) => handleChange(event, "allowPerUser")}
              defaultValue={ticketData?.allowPerUser}
            />
            {errors.allowPerUser && (
              <span className="text-danger">
                {errors.allowPerUser?.message}
              </span>
            )}
          </Form.Group>
        </Row>

        <Row className="mb-3">
          <Form.Group as={Col} controlId="startTime">
            <Form.Label>
              Ticket Sales Start Date<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              {...register("startTime", {
                required: "Start Date is required",
                validate: (value) => {
                  const selectedDate = new Date(value).setHours(0, 0, 0, 0);
                  return (
                    selectedDate >= today ||
                    "Start Date must be greater than or equals to today's date"
                  );
                },
              })}
              defaultValue={ticketData?.startTime}
              min={today}
            />
            {errors.startTime && (
              <span className="text-danger">{errors.startTime?.message}</span>
            )}
          </Form.Group>

          {/* <Form.Group as={Col} controlId="endTime">
            <Form.Label>
              Ticket Sales End Date<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              {...register("endTime", {
                required: "End Date is required",
                validate: (value) => {
                  const selectedDate = new Date(value).setHours(0, 0, 0, 0);
                  const startDate = new Date(getValues("startTime")).setHours(
                    0,
                    0,
                    0,
                    0
                  );
                  if (selectedDate < startDate) {
                    return "End Time must be greater than or equals to Start Date";
                  }
                },
              })}
              defaultValue={ticketData?.endTime}
              min={today.toISOString().split("T")[0]}
            />
            {errors.endTime && (
              <span className="text-danger">{errors.endTime.message}</span>
            )}
          </Form.Group> */}
          <Form.Group as={Col} controlId="endTime">
            <Form.Label>
              Ticket Sales End Date<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              {...register("endTime", {
                required: "End Date is required",
                validate: (value) => {
                  const selectedDate = new Date(value).setHours(0, 0, 0, 0);
                  const startDate = new Date(getValues("startTime")).setHours(
                    0,
                    0,
                    0,
                    0,
                  );

                  if (selectedDate < startDate) {
                    return "End Time must be greater than or equal to Start Date";
                  }

                  if (eventData?.endDate) {
                    const eventEndDate = new Date(eventData.endDate).setHours(
                      0,
                      0,
                      0,
                      0,
                    );
                    if (selectedDate > eventEndDate) {
                      return "Ticket Sales End Date cannot be later than Event End Date";
                    }
                  }
                },
              })}
              defaultValue={getValues("endTime")}
              min={today.toISOString().split("T")[0]}
            />
            {errors.endTime && (
              <span className="text-danger">{errors.endTime?.message}</span>
            )}
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="Image">
            <div className="mb-3">
              <label>Ticket Image</label>
              <div className="d-flex align-items-center">
                <input
                  type="file"
                  className="form-control"
                  accept="image/jpeg, image/png"
                  {...register("Image")}
                  onChange={handleImageChange}
                />
              </div>
              {imageError && <p className="text-danger mt-1">{imageError}</p>}{" "}
              {/* Show validation error below input */}
            </div>

            {imagePreview && (
              <div
                onClick={() => setIsModalVisible(true)}
                style={{ cursor: "pointer" }}
                className="mb-3"
              >
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="img-fluid"
                  style={{ maxWidth: "200px" }}
                />
              </div>
            )}
          </Form.Group>

          <Form.Group
            as={Col}
            controlId="refundable"
            className="mb-3"
            style={{ display: "flex", columnGap: 5, paddingTop: 30 }}
          >
            <Form.Check
              type="checkbox"
              defaultValue={ticketData?.isPublished}
              checked={ticketData?.isPublished} // Controlled by state
              onChange={handleIsStatus} // Handle change event
            />
            <Form.Label>Publish</Form.Label>
          </Form.Group>
        </Row>
        <button
          className="btn btn-primary mt-3"
          type="button"
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </button>
      </Form>
      <Modal show={isModalVisible} onHide={() => setIsModalVisible(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {imagePreview && (
            <img
              src={imagePreview}
              alt="Uploaded Preview"
              className="img-fluid rounded"
            />
          )}
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default injectModels(["application"])(CreateTicket);
